import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X, Gavel, ShoppingBag } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const InlineSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const openDropdown = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      // Escape the narrow search-bar width so results get the full remaining
      // screen width instead of being squeezed to the bar's own width.
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: rect.left,
        width: `calc(100vw - ${rect.left}px - 16px)`,
        maxWidth: 480,
      });
    }
    setIsOpen(true);
  };

  useEffect(() => {
    // Close on click outside
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchProducts();
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const searchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}&limit=8`);
      setResults(response.data);
      openDropdown();
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleResultClick = () => {
    setIsOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} className="flex-1 relative">
      {/* Search Input */}
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && results.length > 0 && openDropdown()}
          placeholder="Buscar productos..."
          className="w-full py-2.5 pl-10 pr-10 bg-[#F1F5F9] border-0 rounded-full text-sm text-[#374151] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#475569]"
          data-testid="header-search-input"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && dropdownStyle && (
        <div
          style={dropdownStyle}
          className="bg-white rounded-2xl shadow-xl border border-[#E2E8F0] max-h-[70vh] overflow-y-auto z-50"
          data-testid="header-search-results"
        >
          {loading ? (
            <div className="p-4 text-center">
              <div className="spinner mx-auto" style={{ width: 24, height: 24 }} />
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-[#64748B]">
              <p className="text-sm">No se encontraron productos para "{query}"</p>
            </div>
          ) : (
            <div className="py-2">
              {results.map((product) => (
                <Link
                  key={product.product_id}
                  to={`/product/${product.product_id}`}
                  onClick={handleResultClick}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-[#F8FAFC] transition-colors"
                  data-testid={`search-result-${product.product_id}`}
                >
                  {/* Product Image */}
                  <img
                    src={product.image_url}
                    alt={product.title}
                    loading="lazy"
                    className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                  />
                  
                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${product.sale_type === 'auction' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {product.sale_type === 'auction' ? 'SUBASTA' : 'VENTA'}
                      </span>
                      {product.sale_type === 'auction' && (
                        <span className="text-[10px] text-[#64748B] flex items-center gap-0.5">
                          <Gavel size={10} />
                          {product.bid_count || 0} ofertas
                        </span>
                      )}
                    </div>
                    <h4 className="font-semibold text-[#0F172A] text-sm leading-snug line-clamp-2 mt-0.5">
                      {product.title}
                    </h4>
                    <p className="text-[#F97316] font-bold text-sm">
                      ${(product.current_bid || product.price)?.toLocaleString()}
                    </p>
                  </div>
                </Link>
              ))}
              
              {/* View all results link */}
              <Link
                to={`/search?q=${encodeURIComponent(query)}`}
                onClick={handleResultClick}
                className="block text-center py-3 text-sm text-[#3B82F6] font-medium hover:bg-[#F8FAFC] border-t border-[#E2E8F0]"
              >
                Ver todos los resultados →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InlineSearch;
